/** Иерархия дерева (блок «Иерархия»): разрезы по порядку, выключенные «глазиком», действия с
 *  разрезами и чтение/запись в адрес. Общая для «Аналитики МП» (`pages/ozon-analytics`, там
 *  `state.ts` реэкспортирует) и «Аналитики ОПТ» (`pages/wholesale`). Коды измерений — белый
 *  список бэкенда раздела; здесь они просто строки. Без React. */

/** Готовый вид дерева: уровни по порядку. */
export interface HierarchyPreset {
  id: string;
  label: string;
  levels: string[];
}

export const MAX_LEVELS = 7;

/** Коды разрезов дерева, которых нет в `meta.dims` площадки (ссылка от другой площадки, старый
 *  код): дерево по ним не строим — показываем ошибку. */
export function unknownLevels(levels: string[], dims: { code: string }[]): string[] {
  return levels.filter((c) => !dims.some((d) => d.code === c));
}

/** Иерархия дерева: все разрезы по порядку + выключенные «глазиком» (по коду). */
export interface Hierarchy {
  levels: string[];
  off: string[];
}

/** Активные уровни — то, что уходит в запрос дерева. */
export function activeLevels(h: Hierarchy): string[] {
  return h.levels.filter((c) => !h.off.includes(c));
}

/** Действия с иерархией (блок «Иерархия»). Нет изменений — возвращается тот же объект. */

/** Смена измерения в строке i. Измерение уже стоит в другой строке — меняем местами
 *  (повторы бэкенд не принимает), признак «выключен» едет вместе с кодом. Свободное
 *  измерение встаёт на место старого кода и наследует его признак (строка не меняет вид). */
export function setLevel(h: Hierarchy, i: number, dim: string): Hierarchy {
  const old = h.levels[i];
  if (old === undefined || old === dim) return h;
  const levels = [...h.levels];
  const j = levels.indexOf(dim);
  if (j !== -1) {
    levels[j] = old;
    levels[i] = dim;
    return { levels, off: h.off };
  }
  levels[i] = dim;
  return { levels, off: h.off.map((c) => (c === old ? dim : c)) };
}

/** Перенос строки from на позицию to (индекс в итоговом списке). */
export function moveLevel(h: Hierarchy, from: number, to: number): Hierarchy {
  if (from === to || from < 0 || to < 0 || from >= h.levels.length || to >= h.levels.length) {
    return h;
  }
  const levels = [...h.levels];
  const [code] = levels.splice(from, 1);
  levels.splice(to, 0, code!);
  return { levels, off: h.off };
}

/** Можно ли выключить/удалить строку: последний активный разрез трогать нельзя. */
function isLastActive(h: Hierarchy, code: string): boolean {
  return !h.off.includes(code) && activeLevels(h).length <= 1;
}

/** Глазик: выключить/включить разрез. */
export function toggleLevelOff(h: Hierarchy, code: string): Hierarchy {
  if (!h.levels.includes(code)) return h;
  if (h.off.includes(code)) return { levels: h.levels, off: h.off.filter((c) => c !== code) };
  if (isLastActive(h, code)) return h;
  return { levels: h.levels, off: [...h.off, code] };
}

export function canRemoveLevel(h: Hierarchy, i: number): boolean {
  const code = h.levels[i];
  return code !== undefined && h.levels.length > 1 && !isLastActive(h, code);
}

export function removeLevel(h: Hierarchy, i: number): Hierarchy {
  if (!canRemoveLevel(h, i)) return h;
  const levels = h.levels.filter((_, k) => k !== i);
  return { levels, off: h.off.filter((c) => levels.includes(c)) };
}

/** Первое свободное измерение — после строки afterIndex, без неё — в конец; лимит — все строки. */
export function addLevel(h: Hierarchy, allDims: string[], afterIndex?: number): Hierarchy {
  if (h.levels.length >= MAX_LEVELS) return h;
  const free = allDims.find((d) => !h.levels.includes(d));
  if (!free) return h;
  const at =
    afterIndex !== undefined && afterIndex >= 0 && afterIndex < h.levels.length
      ? afterIndex + 1
      : h.levels.length;
  const levels = [...h.levels];
  levels.splice(at, 0, free);
  return { levels, off: h.off };
}

/** Адрес → иерархия: `lv` — свои уровни через запятую (как есть: чужие коды страница показывает
 *  ошибкой через `unknownLevels`), иначе пресет `tp` (нет / неизвестный — первый); `lvoff` —
 *  выключенные разрезы (чужие коды и повторы отбрасываются; выключены все — не действует).
 *  `preset` — id пресета, если уровни совпали с ним и выключенных нет. */
export function readHierarchy(
  params: URLSearchParams,
  presets: readonly HierarchyPreset[],
): Hierarchy & { preset: string | null } {
  const first = presets[0];
  if (!first) throw new Error("нет ни одного пресета дерева");
  const lv = params.get("lv");
  let levels: string[];
  let preset: string | null;
  if (lv) {
    levels = lv.split(",").filter(Boolean);
    preset = presets.find((p) => p.levels.join(",") === levels.join(","))?.id ?? null;
  } else {
    const p = presets.find((x) => x.id === params.get("tp")) ?? first;
    levels = [...p.levels];
    preset = p.id;
  }
  const lvoff = (params.get("lvoff") ?? "").split(",");
  let off = levels.filter((c, i) => levels.indexOf(c) === i && lvoff.includes(c));
  if (off.length > 0 && levels.every((c) => off.includes(c))) off = [];
  if (off.length > 0) preset = null;
  return { levels, off, preset };
}

/** Пресет дерева → адрес (первый пресет — без параметра; выключенные разрезы сбрасываются). */
export function hierarchyPresetPatch(
  id: string,
  presets: readonly HierarchyPreset[],
): Record<string, string | null> {
  return { tp: id === presets[0]?.id ? null : id, lv: null, lvoff: null };
}

/** Иерархия → адрес: совпала с пресетом и ничего не выключено — пишем пресет, иначе `lv`/`lvoff`. */
export function hierarchyPatch(
  h: Hierarchy,
  presets: readonly HierarchyPreset[],
): Record<string, string | null> {
  const off = h.levels.filter((c, i) => h.levels.indexOf(c) === i && h.off.includes(c));
  const p = presets.find((x) => x.levels.join(",") === h.levels.join(","));
  if (p && off.length === 0) return hierarchyPresetPatch(p.id, presets);
  return { tp: null, lv: h.levels.join(","), lvoff: off.length ? off.join(",") : null };
}
